import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
    Box,
    Typography,
    Button,
    Grid,
    Divider,
    useTheme
} from "@mui/material";
import { getIconFromDriverName } from "./iconFunctions";
import { formatString } from "./valueFunctions";
import ChatInterviewModal from "./ChatInterviewModal";
import ChatEfficiencyDriverForm from "./ChatEfficiencyDriverForm";

export default function OutcomeDriverList({
    valueModel,
    driver_analysis,
    selectedOutcome,
    selectedOutcomeExplanation,
    selectedDriver,
    driverConfig,
    onAddDriver,
}) {
    const theme = useTheme();

    const [modalOpen, setModalOpen] = useState(false);
    const [modalParams, setModalParams] = useState({
        mode: "personnel",
        category: "",
        subjectType: "",
        driver: "",
    });

    const { driverPath, type: costType, name: driverNameLabel } = driverConfig[selectedDriver] || {};

    const getSavedEntry = (category, type) => {
        const targetArray = selectedDriver === "personnel_costs"
            ? valueModel?.cost_value_driver?.labor_savings || []
            : valueModel?.cost_value_driver?.[driverPath] || [];
        return targetArray.find(
            (entry) =>
                entry.outcome === selectedOutcome &&
                entry.sub_cost === category &&
                entry.multiple_type === type
        );
    };

    const handleAddClick = ({ mode, category, subjectType, driver }) => {
        setModalParams({ mode, category, subjectType, driver });
        setModalOpen(true);
    };

    const industry_description = useSelector(state => state.value.costStructure.description)

    const operationalEntry = useSelector(state =>
        state.value.costStructure.cost_structure[selectedDriver][
        modalParams.category.replace(' ', '_')
        ]
    )

    // Find the matching outcome block
    const outcomeBlock = useMemo(() => {
        for (const section of [
            "cost_savings",
            "extra_costs",
            "revenue_enhancements",
            "risk_lowering",
        ]) {
            for (const oc of driver_analysis[section] || []) {
                if (oc.title === selectedOutcome) {
                    return oc;
                }
            }
        }
        return null;
    }, [driver_analysis, selectedOutcome]);

    const outcomeSection = useMemo(() => {
        for (const section of [
            "cost_savings",
            "extra_costs",
            "revenue_enhancements",
            "risk_lowering",
        ]) {
            const list = driver_analysis[section];
            if (Array.isArray(list) && list.some((oc) => oc.title === selectedOutcome)) {
                return section;
            }
        }
        return null;
    }, [driver_analysis, selectedOutcome]);

    const positive_driver = outcomeSection === "extra_costs" ? false : true;

    // Group affected_types by category
    const grouped = useMemo(() => {
        if (!outcomeBlock) return [];
        const map = {};
        for (const drv of outcomeBlock.drivers) {
            if (drv.driver !== selectedDriver) continue;
            for (const { category, type } of drv.affected_types || []) {
                if (!map[category]) map[category] = new Set();
                map[category].add(type);
            }
        }
        return Object.entries(map).map(([cat, types]) => [cat, Array.from(types)]);
    }, [outcomeBlock, selectedDriver]);

    // Nothing to show?
    if (!grouped.length) return null;

    // Decide intro text based on driver type
    const isOperational = driverConfig.hasOwnProperty(selectedDriver);

    // Multiple and frequency can safely branch:
    const multiple =
        isOperational && operationalEntry
            ? operationalEntry.multiple
            : ''

    const frequency =
        isOperational && operationalEntry
            ? operationalEntry.frequency
            : ''

    const introText = isOperational
        ? `The following cost categories and types were identified as potentially impacted by this outcome—click “Add Driver” to quantify cost and efficiency ${positive_driver ? "gain" : "loss"}.`
        : `The following staff categories and roles were identified as potentially impacted by this outcome—click “Add Driver” to quantify ${positive_driver ? "hours saved" : "extra hours"} per employee.`;

    return (
        <Box sx={{ maxWidth: 800 }}>
            {/* Highlighted intro */}
            <Box
                sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: theme.palette.grey[200],
                    borderRadius: 1,
                }}
            >
                {/* <Typography variant="body1" sx={{ fontWeight: "bold" }}>
          {isOperational ? "Operational Cost Drivers" : "Personnel Efficiency Drivers"}
        </Typography> */}
                <Typography variant="body2">{introText}</Typography>
            </Box>

            {/* Category / Type list */}
            {grouped.map(([category, types]) => (
                <Box key={category} mb={4}>
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                        {formatString(category)}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {types.map((type) => {
                        const saved = getSavedEntry(category, type);

                        return (
                            <Grid
                                container
                                alignItems="center"
                                columnSpacing={2}
                                key={type}
                                sx={{ mb: 3 }}  // extra space between rows
                            >
                                <Grid item xs={3}>
                                    <Typography>{formatString(type)}</Typography>
                                </Grid>
                                <Grid item xs={4} sx={{ textAlign: "right" }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        sx={{ textTransform: "none" }}
                                        startIcon={<Box component={getIconFromDriverName(selectedDriver)} />}
                                        onClick={() =>
                                            handleAddClick({
                                                mode: isOperational ? "operational" : "personnel",
                                                category,
                                                subjectType: type,
                                                driver: selectedDriver,
                                            })
                                        }
                                        disabled={Boolean(saved)}
                                    >
                                        {saved ? "Saved" : "Add Driver"}
                                    </Button>
                                </Grid>
                                <Grid item xs={5} />
                            </Grid>
                        )
                    })}
                </Box>
            ))}
            <ChatInterviewModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                industry={industry_description}
                outcome={selectedOutcomeExplanation}
                mode={modalParams.mode}
                category={modalParams.category}
                subjectType={modalParams.subjectType}
                driver={modalParams.driver}
                driverConfig={driverConfig}
                multiple={multiple}
                frequency={frequency}
                positive_driver={positive_driver}
                FormComponent={ChatEfficiencyDriverForm}
                formProps={{
                    onSubmit: (payload, { setSubmitting, setErrors }) => {
                        setSubmitting(true);

                        const basePayload = {
                            outcome: selectedOutcome,
                            cost: selectedDriver,
                            cost_type: isOperational ? costType : 'labor',
                            driverPath: isOperational ? driverPath : 'labor_savings',
                            driver_name: `${formatString(
                                modalParams.subjectType
                            )} ${positive_driver ? "Savings" : "Extra"}`,
                            comment: "",
                            currency: "€",
                            sub_cost: modalParams.category,
                            multiple_type: modalParams.subjectType,
                            ...(isOperational
                                ? { share_of_cost: 100 }
                                : { occurrence: 100 }),
                            // range: formRange,
                            // low,
                            // high,
                            ...payload,
                            type: null,
                            multiple: multiple,
                            frequency: frequency,
                        };
                        
                        onAddDriver(basePayload, { setSubmitting, setErrors });
                        setSubmitting(false);
                        setModalOpen(false);
                    }
                }}
            />
        </Box>
    );
}

OutcomeDriverList.propTypes = {
    valueModel: PropTypes.object.isRequired,
    driver_analysis: PropTypes.object.isRequired,
    selectedOutcome: PropTypes.string.isRequired,
    selectedDriver: PropTypes.string.isRequired,
    driverConfig: PropTypes.object.isRequired,
    onAddDriver: PropTypes.func.isRequired,
};
